import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Observable, switchMap, catchError, take, combineLatest, finalize } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';

import { UserByUuidResponse } from '../models/user.model';
import {
  LoginData,
  LoginRequest,
  LoginResponse,
  MobileLoginRequest,
  MobileLoginResponse,
} from '../models/login.model';
import { Draft } from '../models/order.model';
import { UserFacade } from '../store/user/user.facade';
import { CartFacade } from '../store/cart/cart.facade';
import { loadCartFromDraft } from '../store/cart/cart.actions';
import {
  isCartHydrationBlocked,
  unblockCartHydration,
} from '../utils/cart-hydration.util';
import { shouldSkipCampaignCartHydration } from '../campaign/campaign-cart-reset';
import { readStoredExternalChannelCampaign } from '../utils/external-channel-access.util';
import { buildLoginDataFromAuthPing } from '../utils/auth-ping-login-data.util';
import { selectCartItems } from '../store/cart/cart.selectors';
import { CartItem } from '../store/cart/cart.state';
import { AppState } from '../store/app.state';
import { unformatCnpj } from '../utils/cnpj.util';

import { ApiService } from './api.service';
import { OrderService } from './order.service';
import { LoginService } from './login.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly STORAGE_KEY = 'umkt_login_data';
  private logoutTimer: NodeJS.Timeout | null = null;
  private isDraftLoading = false;
  private lastCartModification = 0;

  private loginService = inject(LoginService);
  private userFacade = inject(UserFacade);
  private cartFacade = inject(CartFacade);
  private orderService = inject(OrderService);
  private apiService = inject(ApiService);
  private platformId = inject(PLATFORM_ID);
  private store = inject<Store<AppState>>(Store);

  constructor() {
    this.setupStorageListener();
  }

  private resetLoadingOnError(error: unknown): void {
    console.error('Erro detectado, resetando loading:', error);
    this.userFacade.setLoading(false);

    const status = (error as { status?: unknown })?.status;
    if (status !== 'success') {
      this.userFacade.loginFailure(
        (error as { message?: unknown })?.message && typeof (error as { message?: unknown })?.message === 'string'
          ? ((error as { message?: unknown })?.message as string)
          : 'Erro no processo de login'
      );
    }
  }

  login(credentials: LoginRequest): Observable<{
    loginResponse: LoginResponse;
    userDetails: UserByUuidResponse;
  }> {
    this.userFacade.login(credentials);

    return this.loginService.login(credentials).pipe(
      switchMap(loginResponse => {
        if (loginResponse.status === 'success') {
          this.userFacade.loginSuccess(loginResponse);

          this.saveLoginData(loginResponse.data);

          const userUuid = loginResponse.data.userUuid;
          this.userFacade.loadUserDetails(userUuid);

          this.checkAndLoadDraftIfNeeded(userUuid);

          return new Observable<{
            loginResponse: LoginResponse;
            userDetails: UserByUuidResponse;
          }>(observer => {
            observer.next({
              loginResponse,
              userDetails: {
                status: 'success',
                data: null,
              } as unknown as UserByUuidResponse,
            });
            observer.complete();
          });
        } else {
          this.resetLoadingOnError(loginResponse);
          throw new Error(loginResponse.msg || 'Login falhou');
        }
      }),
      catchError(error => {
        console.error('Erro no login:', error);
        this.resetLoadingOnError(error);
        throw error;
      })
    );
  }

  mobileLogin(credentials: MobileLoginRequest): Observable<{
    mobileLoginResponse: MobileLoginResponse;
    userDetails: UserByUuidResponse;
  }> {
    this.userFacade.login(credentials);

    return this.loginService.loginMobile(credentials).pipe(
      switchMap(mobileLoginResponse => {
        const loginResponse: LoginResponse = {
          status: 'success',
          msg: 'Login realizado com sucesso',
          error: false,
          errors: null,
          page: null,
          nextPage: null,
          lastPage: null,
          nextPageUrl: null,
          data: {
            loginAccess: mobileLoginResponse.user.email,
            loginDate: new Date().toISOString(),
            userUuid: mobileLoginResponse.user.uuid,
            sessionId: mobileLoginResponse.sessionId,
            isFirstAccess: mobileLoginResponse.isFirstAccess,
            resources: mobileLoginResponse.resources,
          },
        };

        this.userFacade.loginSuccess(loginResponse);
        this.saveLoginData(loginResponse.data);

        this.userFacade.loadUserDetailsSuccess({
          status: 'success',
          data: mobileLoginResponse.user,
        } as UserByUuidResponse);

        const userUuid = loginResponse.data.userUuid;
        this.checkAndLoadDraftIfNeeded(userUuid);

        return new Observable<{
          mobileLoginResponse: MobileLoginResponse;
          userDetails: UserByUuidResponse;
        }>(observer => {
          observer.next({
            mobileLoginResponse,
            userDetails: { status: 'success', data: mobileLoginResponse.user } as UserByUuidResponse,
          });
          observer.complete();
        });
      }),
      catchError(error => {
        console.error('Erro no login móvel:', error);
        this.resetLoadingOnError(error);
        throw error;
      })
    );
  }

  logout(): void {
    combineLatest([
      this.userFacade.getLoginData(),
      this.userFacade.getUserDetails(),
    ])
      .pipe(take(1))
      .subscribe(([storeLoginData, userDetails]) => {
        const loginData = storeLoginData
          ? {
              ...storeLoginData,
              document: storeLoginData.document || userDetails?.document,
            }
          : this.getLoginData();

        if (this.shouldLogoutFixa(loginData)) {
          this.logoutFixaCnpj(loginData);
          return;
        }

        this.finalizeLogout();
      });
  }

  logoutFixaByCnpj(cnpj: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const normalized = unformatCnpj(cnpj);
    if (!normalized) {
      console.warn('[FIXA] Documento inválido ao chamar logout por documento:', cnpj);
      return;
    }
    this.apiService
      .post<void>(`/user/ping/logout/${encodeURIComponent(normalized)}`, {})
      .pipe(take(1))
      .subscribe({
        error: (err: unknown) =>
          console.warn('[FIXA] Falha ao deslogar por CNPJ (ping error):', err),
      });
  }

  private logoutFixaCnpj(loginData: LoginData): void {
    if (!isPlatformBrowser(this.platformId) || !this.shouldLogoutFixa(loginData)) {
      this.finalizeLogout();
      return;
    }

    const cnpj = unformatCnpj(loginData.document);

    if (!cnpj || cnpj.length !== 14) {
      console.warn('[FIXA] CNPJ inválido ao tentar logout:', loginData.document);
      this.finalizeLogout();
      return;
    }

    this.apiService
      .post<void>(`/user/ping/logout/${encodeURIComponent(cnpj)}`, {})
      .pipe(
        take(1),
        finalize(() => this.finalizeLogout())
      )
      .subscribe({
        error: (err: unknown) =>
          console.warn('[FIXA] Falha ao deslogar por CNPJ:', err),
      });
  }

  private shouldLogoutFixa(
    loginData: LoginData | null | undefined
  ): loginData is LoginData {
    if (!loginData?.sessionId) {
      return false;
    }

    const firstResource = Array.isArray(loginData.resources)
      ? loginData.resources[0]
      : null;

    return !!firstResource &&
      typeof firstResource === 'object' &&
      'customerAccountId' in firstResource;
  }

  private finalizeLogout(): void {
    this.clearLogoutTimer();
    this.clearLoginData();
    unblockCartHydration();
    this.userFacade.logout();
    this.userFacade.logoutSuccess();
  }
  
  private clearLogoutTimer(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  private saveLoginData(loginData: LoginData): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const dataWithTimestamp = {
          ...loginData,
          lastActivity: Date.now(),
        };
        localStorage.setItem(
          this.STORAGE_KEY,
          JSON.stringify(dataWithTimestamp)
        );
      } catch (error) {
        console.error('Erro ao salvar dados de login:', error);
      }
    }
  }

  getLoginData(): LoginData | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const saved = localStorage.getItem(this.STORAGE_KEY);
        return saved ? JSON.parse(saved) : null;
      } catch (error) {
        console.error('Erro ao recuperar dados de login:', error);
        return null;
      }
    }
    return null;
  }

  private clearLoginData(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private isSessionValid(loginDate: string, lastActivity?: number): boolean {
    if (!lastActivity) {
      return true;
    }

    const now = Date.now();
    const inactivityLimit = 1440 * 60 * 1000;
    const timeSinceLastActivity = now - lastActivity;

    return timeSinceLastActivity < inactivityLimit;
  }

  private setupStorageListener(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key === this.STORAGE_KEY) {
          if (event.newValue === null) {
            this.userFacade.logout();
            this.userFacade.logoutSuccess();
            this.clearLogoutTimer();
          }
        }
      });
    }
  }

  initializeAuthState(): void {
    const savedLoginData = this.getLoginData();

    if (!savedLoginData) {
      return;
    }

    if (!savedLoginData.loginDate) {
      this.clearLoginData();
      return;
    }

    const isValid = this.isSessionValid(
      savedLoginData.loginDate,
      savedLoginData.lastActivity
    );

    if (!isValid) {
      this.clearLoginData();
      return;
    }

    const resources = savedLoginData.resources;
    const firstResource = Array.isArray(resources) && resources.length > 0 ? resources[0] : null;
    const isFixaMode =
      !!savedLoginData.sessionId &&
      firstResource &&
      typeof firstResource === 'object' &&
      'customerAccountId' in firstResource;

    if (isFixaMode && savedLoginData.sessionId) {
      // Sessão FIXA: ao recarregar a página, revalida a sessão usando o sessionId.
      this.loginService
        .authPing(savedLoginData.sessionId)
        .pipe(take(1))
        .subscribe({
          next: response => {
            this.handleAuthPingSuccess(response, savedLoginData);
          },
          error: error => {
            console.error('Erro ao revalidar sessão FIXA com sessionId:', error);
            this.clearLoginData();
            this.userFacade.logout();
            this.userFacade.logoutSuccess();
          },
        });
      return;
    }

    // Fluxo padrão (login "normal" com userUuid conhecido no login)
    this.userFacade.loginSuccess({
      status: 'success',
      msg: 'Sessão restaurada',
      error: false,
      errors: null,
      page: null,
      nextPage: null,
      lastPage: null,
      nextPageUrl: null,
      data: savedLoginData,
    });

    if (savedLoginData.userUuid) {
      this.userFacade.loadUserDetails(savedLoginData.userUuid);
      this.checkAndLoadDraftIfNeeded(savedLoginData.userUuid);
    }
  }

  getCurrentUserUuid(): string | null {
    const loginData = this.getLoginData();
    return loginData?.userUuid || null;
  }

  getCurrentSessionId(): string | null {
    const loginData = this.getLoginData();
    return loginData?.sessionId || null;
  }

  isFixaSession(): boolean {
    return this.shouldLogoutFixa(this.getLoginData());
  }

  updateStoredLoginData(partial: Partial<LoginData>): void {
    const current = this.getLoginData();
    if (!current) {
      return;
    }
    const updated: LoginData = {
      ...current,
      ...partial,
    };
    this.saveLoginData(updated);
    this.userFacade.updateLoginData(partial);
  }

  /**
   * Sincroniza o CNPJ selecionado em currentUser, loginData (store) e storage.
   * Usado no login móvel e no bootstrap SUE do external channel.
   */
  syncSelectedDocument(document: string): void {
    const clean = unformatCnpj(document);
    if (!clean) {
      return;
    }

    this.userFacade.selectCnpj(clean);
    this.updateStoredLoginData({ document: clean });
  }

  persistLoginData(loginData: LoginData): void {
    this.saveLoginData(loginData);
    this.userFacade.updateLoginData(loginData);
  }

  handleAuthPingSuccess(
    response: unknown,
    previousLoginData?: LoginData | null
  ): void {
    const loginData = buildLoginDataFromAuthPing(response, previousLoginData);

    if (!loginData) {
      console.warn('AuthPing: resposta sem sessionId', response);
      return;
    }

    this.saveLoginData(loginData);
    this.userFacade.loginSuccess({
      status: 'success',
      msg: 'Sessão restaurada',
      error: false,
      errors: null,
      page: null,
      nextPage: null,
      lastPage: null,
      nextPageUrl: null,
      data: loginData,
    });

    // Se o contrato já havia sido selecionado, reidrata o usuário pelo UUID preservado.
    // Sem UUID, currentUser permanece null até a seleção de contrato (estado esperado).
    if (loginData.userUuid) {
      this.userFacade.loadUserDetails(loginData.userUuid);
      this.checkAndLoadDraftIfNeeded(loginData.userUuid);
    }
  }

  isAuthenticated(): boolean {
    const loginData = this.getLoginData();
    if (!loginData?.loginDate) {
      return false;
    }
    return this.isSessionValid(loginData.loginDate, loginData.lastActivity);
  }

  private checkAndLoadDraftIfNeeded(userUuid: string): void {
    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return;
    }

    const storedItems = this.cartFacade.loadCartFromLocalStorage();
    if (readStoredExternalChannelCampaign() && storedItems.length === 0) {
      this.cartFacade.clearCart();
      return;
    }
    if (storedItems.length > 0) {
      this.sendStoredCartToDraft(userUuid, storedItems);
    }
  }

  private sendStoredCartToDraft(
    userUuid: string,
    storedItems: CartItem[]
  ): void {
    const draftRequest: Draft = {
      userUuid: userUuid,
      draftOrderUuid: null,
      orderItems: storedItems
        .filter(item => item.selectedVariation || item.product?.productType === 'MIND')
        .map(item => ({
          product: item.product,
          quantity: item.quantity,
          ...(item.selectedVariation && {
          selectedVariation: item.selectedVariation
        })
      })),
    };

    this.orderService.createOrderDraft(draftRequest).subscribe({
      next: _response => {
        this.cartFacade.clearCartFromLocalStorage();
        this.loadDraftFromBackend(userUuid);
      },
      error: error => {
        console.error('Erro ao criar draft:', error);
        this.loadDraftFromBackend(userUuid);
      },
    });
  }

  markCartAsModified(): void {
    this.lastCartModification = Date.now();
  }

  private loadDraftFromBackend(userUuid: string): void {
    if (this.isDraftLoading) {
      return;
    }

    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return;
    }

    const now = Date.now();
    const timeSinceModification = now - this.lastCartModification;

    if (timeSinceModification < 5000) {
      return;
    }

    this.store
      .select(selectCartItems)
      .pipe(take(1))
      .subscribe(items => {
        if ((items?.length ?? 0) > 0) {
          console.debug('[DRAFT] Ignorado: store não está vazio');
          return;
        }

        this.isDraftLoading = true;
            this.cartFacade.setLoading(true);
        this.orderService
          .getOrderDraftByUuid(userUuid)
              .pipe(
                take(1),
                finalize(() => {
                  this.isDraftLoading = false;
                  this.cartFacade.setLoading(false);
                })
              )
          .subscribe({
            next: draft => {
              if (draft?.orderItems?.length) this.loadDraftToCart(draft);
            },
            error: error => {
              console.error('Erro ao buscar draft do backend:', error);
            },
          });
      });
  }

  private loadDraftToCart(draft: Draft): void {
    if (draft.orderItems && draft.orderItems.length > 0) {
      this.store.dispatch(loadCartFromDraft({ draft }));
    }
  }
}
